import { IntegratedDialog } from "@tedivo/tedivo-ui";
import Services from "../../../../app/services";
import { TedivoForm } from "@tedivo/tedivo-form";
import { getTranslation } from "../../../../app/i18/i18tn";
import { millimetersToFeet } from "@tedivo/tedivo-pure-helpers";
import { z } from "zod";

export function createModalToExportViewsToImages(
  title: string,
  shipName: string | undefined,
  view: "side" | "deck",
) {
  return () =>
    new Promise<void>((resolve) => {
      const modal = new IntegratedDialog<"export" | "cancel">({
        parentNode: document.body,
        width: "700px",
        buttonsAttrs: [
          {
            label: getTranslation("general:common.cancel"),
            variant: "neutral",
            type: "button",
            value: "cancel",
          },
          {
            label: getTranslation("general:common.export"),
            variant: "primary",
            type: "submit",
            value: "export",
          },
        ],
        removeDialogWhenHidden: true,
      });

      const form = new TedivoForm<IFormExport>({
        fields: [
          {
            name: "exportType",
            label: getTranslation("view:exportViews.exportType"),
            type: "radioButtonList",
            options: [
              {
                value: "png",
                label: getTranslation("view:exportViews.exportTypeOptions.png"),
              },
              {
                value: "jpeg",
                label: getTranslation(
                  "view:exportViews.exportTypeOptions.jpeg",
                ),
              },
              {
                value: "pct",
                label: getTranslation("view:exportViews.exportTypeOptions.pct"),
              },
            ],
            initialValue: "png",
            fieldset: true,
          },
          [
            {
              name: "withContainers",
              label: getTranslation("view:exportViews.withContainers"),
              type: "checkbox",
              initialValue: true,
            },
          ],
          {
            name: "withTopBayNumbers",
            label: getTranslation("view:exportViews.withTopBayNumbers"),
            type: "checkbox",
            initialValue: true,
          },
          {
            name: "scale1Mt1px",
            label: getTranslation("view:exportViews.scale1Mt1px"),
            type: "number",
            initialValue: 10,
            helpText: getTranslation("view:exportViews.scale1Mt1pxHelpText"),
          },
          [
            {
              name: "translateX",
              label: getTranslation("view:exportViews.translateX"),
              type: "number",
              initialValue: 0,
            },
            {
              name: "translateY",
              label: getTranslation("view:exportViews.translateY"),
              type: "number",
              initialValue: 0,
            },
          ],

          {
            type: "hidden",
            name: "view",
            initialValue: view,
          },
        ],
        submitButton: document.createElement("sl-button"),
        formProps: { autoFocusOnFirstInput: false },
        formValidator: createValidator(),
        hiddenData: { view },
        onSubmit: () => undefined,
      });

      form.onDataChange = (vs, nm) => {
        if (nm === "exportType" && vs.exportType === "pct") {
          // If PCT is selected, we need to ensure that the scale and translation are set
          form.setValue("scale1Mt1px", 0.8);
        }
      };

      modal.onButtonClicked = (action) => {
        switch (action) {
          case "export": {
            const isValid = form.execValidation();
            console.log("isValid", isValid);

            if (isValid.success) {
              const vals = form.getValues();

              convertToImageAndDownload({
                ...vals,
                shipName,
              });

              resolve();
              modal.hide();
            } else {
              return false;
            }
            break;
          }
          case "cancel":
            resolve();
            modal.hide();
            break;
        }
      };

      modal.show(title, form.form);
    });
}

function createValidator() {
  return z.object({
    exportType: z.enum(["png", "svg", "pct", "jpeg"]),
    view: z.enum(["side", "deck"]),
    withContainers: z.boolean(),
    withTopBayNumbers: z.boolean(),
    scale1Mt1px: z.number().min(0.1).max(1000),
    translateX: z.number().min(0).max(10000),
    translateY: z.number().min(0).max(10000),
  });
}

interface IFormExport {
  exportType: "png" | "svg" | "pct" | "jpeg";
  view: "side" | "deck";
  withContainers: boolean;
  withTopBayNumbers: boolean;
  scale1Mt1px: number;
  translateX: number;
  translateY: number;
}

function convertToPngOrJpeg({
  view,
  exportType,
  withContainers,
  withTopBayNumbers,
  scale1Mt1px,
  translateX,
  translateY,
}: IFormExport) {
  const svgId = view === "side" ? "sideViewSvg" : "deckViewSvg";
  const svg = document.getElementById(svgId) as SVGSVGElement | null;
  if (!svg) {
    return;
  }

  const svgWidth = svg.getAttribute("width");
  const lengthInMeters = Number(svg.getAttribute("data-length-meters"));

  if (!svgWidth || !lengthInMeters) {
    console.error("SVG element does not have a svgWidth attribute");
    return;
  }

  const imgWidth = (lengthInMeters / 1000) * (scale1Mt1px || 1);

  return new Promise<string>((resolve) => {
    const svgUsed = svg.cloneNode(true) as SVGSVGElement;

    const w = svg.clientWidth;
    const h = svg.clientHeight;
    const wRatio = w / h;

    const canvas = document.createElement("canvas");
    canvas.width = imgWidth + translateX;
    canvas.height = Math.ceil(imgWidth / wRatio) + translateY + 20;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    if (!withContainers) {
      const container = svgUsed.querySelector("g#Bays");
      container?.setAttribute("visibility", "hidden");
    }

    if (!withTopBayNumbers) {
      const gTopBayNumbers = svgUsed.querySelector("g.top-bay-numbers");
      gTopBayNumbers?.setAttribute("visibility", "hidden");
    }

    const svgData = new XMLSerializer().serializeToString(svgUsed);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.fill();
      ctx.drawImage(
        img,
        translateX,
        translateY,
        canvas.width - translateX,
        canvas.height - translateY - 20,
      );
      const pngData = canvas.toDataURL(`image/${exportType}`);
      resolve(pngData);
      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      console.error("Failed to load SVG image");
    };

    img.crossOrigin = "anonymous";
    img.src = url;
  });
}

function preparePctAsPng({
  view,
  withContainers,
  withTopBayNumbers,
  scale1Mt1px,
  translateX,
  translateY,
}: IFormExport) {
  const svgId = view === "side" ? "sideViewSvg" : "deckViewSvg";
  const svg = document.getElementById(svgId) as SVGSVGElement | null;
  if (!svg) {
    console.error("SVG element not found");
    return;
  }

  const svgWidth = svg.getAttribute("width");
  const lengthInMeters = svg.getAttribute("data-length-meters");

  if (!svgWidth || !lengthInMeters) {
    console.error("SVG element does not have a svgWidth attribute");
    return;
  }

  const lengthInFeet = millimetersToFeet(parseFloat(lengthInMeters));
  const requiredWidthForSE = scale1Mt1px * lengthInFeet;

  return new Promise<string>((resolve) => {
    const svgUsed = svg.cloneNode(true) as SVGSVGElement;

    const w = svg.clientWidth;
    const h = svg.clientHeight;
    const wRatio = w / h;

    const canvas = document.createElement("canvas");
    canvas.width = requiredWidthForSE + translateX;
    canvas.height = Math.ceil(requiredWidthForSE / wRatio) + translateY;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Failed to get canvas context");
      return;
    }

    if (!withContainers) {
      const container = svgUsed.querySelector("g#Bays");
      container?.setAttribute("visibility", "hidden");
    }

    if (!withTopBayNumbers) {
      const gTopBayNumbers = svgUsed.querySelector("g.top-bay-numbers");
      gTopBayNumbers?.setAttribute("visibility", "hidden");
    }

    const svgData = new XMLSerializer().serializeToString(svgUsed);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.fill();
      ctx.drawImage(
        img,
        translateX,
        translateY,
        canvas.width - translateX,
        canvas.height - translateY,
      );
      const pngData = canvas.toDataURL("image/png");
      resolve(pngData);
      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      console.error("Failed to load SVG image");
    };

    img.crossOrigin = "anonymous";
    img.src = url;
  });
}

async function convertToImageAndDownload({
  exportType,
  view,
  withContainers,
  withTopBayNumbers,
  shipName,
  scale1Mt1px,
  translateX,
  translateY,
}: IFormExport & {
  shipName: string | undefined;
}) {
  switch (exportType) {
    case "pct":
      {
        const pngData = await preparePctAsPng({
          exportType,
          view,
          withContainers,
          withTopBayNumbers,
          scale1Mt1px,
          translateX,
          translateY,
        });
        if (!pngData) {
          console.error("Failed to convert SVG to PNG");
          return;
        }

        const pctData = await Services.files.convertPngtoPct(pngData);
        if (!pctData || !pctData.data) {
          console.error("Failed to convert PNG to PCT");
          return;
        }

        const a = document.createElement("a");
        a.href = pctData.data;
        a.download = `${shipName || "-"}-${view}View.pct`;
        a.click();
      }
      return;
    case "png":
      {
        const pngData = await convertToPngOrJpeg({
          view,
          exportType: "png",
          withContainers,
          withTopBayNumbers,
          scale1Mt1px,
          translateX,
          translateY,
        });

        if (!pngData) {
          console.error("Failed to convert SVG to PNG");
          return;
        }

        const a = document.createElement("a");
        a.href = pngData;
        a.download = `${shipName || "-"}-${view}View.png`;
        a.click();
      }
      break;
    case "jpeg":
      {
        const pngData = await convertToPngOrJpeg({
          view,
          exportType: "jpeg",
          withContainers,
          withTopBayNumbers,
          scale1Mt1px,
          translateX,
          translateY,
        });

        if (!pngData) {
          console.error("Failed to convert SVG to JPEG");
          return;
        }

        const a = document.createElement("a");
        a.href = pngData;
        a.download = `${shipName || "-"}-${view}View.jpg`;
        a.click();
      }
      break;
  }
}
